import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ProgressBar from './ProgressBar';
import Button from './Button';
import QuestionLabel from './QuestionLabel';

const Loading = ({
	messages,
	question,
	progressDuration = 30000,
	className = '',
	onComplete,
	completed = false,
	...props
}) => {
	const { t } = useTranslation();
	const [progress, setProgress] = useState(0);
	const [isComplete, setIsComplete] = useState(false);

	const displayMessages = useMemo(() => (messages ? messages.map((key) => t(key)) : []), [messages, t]);

	const progressRef = useRef(0);
	const startTimeRef = useRef(null);

	const messageIndex = useMemo(() => {
		const messageCount = displayMessages.length;
		if (messageCount === 0) return null;

		const progressPerMessage = 100 / messageCount;
		const index = Math.floor(progress / progressPerMessage);
		return Math.min(index, messageCount - 1);
	}, [progress, displayMessages.length]);

	useEffect(() => {
		const calculateProgress = (elapsed) => {
			if (completed) {
				return Math.min(progressRef.current + 2, 100);
			}

			const timeRatio = elapsed / progressDuration;

			if (timeRatio <= 0.6) {
				return Math.min((timeRatio / 0.6) * 95, 95);
			} else if (timeRatio <= 0.75) {
				return 95;
			} else {
				return 97;
			}
		};

		if (isComplete) return;

		if (!startTimeRef.current) {
			startTimeRef.current = Date.now();
		}

		const progressTimer = setInterval(() => {
			const now = Date.now();
			const elapsed = now - startTimeRef.current;
			const targetProgress = calculateProgress(elapsed);

			// Only update if progress has actually changed
			if (targetProgress !== progressRef.current) {
				progressRef.current = targetProgress;
				setProgress(targetProgress);
			}

			if (targetProgress >= 100) {
				setIsComplete(true);
				if (onComplete) {
					onComplete();
				}
				clearInterval(progressTimer);
			}
		}, 50);

		return () => clearInterval(progressTimer);
	}, [progressDuration, onComplete, isComplete, completed]);

	useEffect(() => {
		return () => {
			progressRef.current = 0;
			startTimeRef.current = null;
		};
	}, []);

	const handleComplete = useCallback(() => {
		setIsComplete(true);
		if (onComplete) {
			onComplete();
		}
	}, [onComplete]);

	return (
		<div className={`flex flex-col items-center justify-center w-full ${className}`} {...props}>
			{question && (
				<div className='flex flex-col items-start gap-2 max-w-2xl mb-8 text-left'>
					<QuestionLabel />
					<h1 className='text-xl font-medium text-on-secondary tracking-wide line-clamp-3'>{question}</h1>
				</div>
			)}
			<div className='mb-8 flex flex-col items-center'>
				<img src='/images/Socrates_ikon.png' alt='Logo' className='max-h-80 w-auto mb-4' />
			</div>
			{messageIndex !== null && (
				<div className='text-center mb-8'>
					<div className='text-lg font-medium text-gray-700 mb-2'>{displayMessages[messageIndex]}</div>
					<div className='text-sm text-gray-500'>{t('loading.please_wait')}</div>
				</div>
			)}
			<ProgressBar progress={progress} label={t('loading.loading')} />
			{isComplete && !onComplete && (
				<Button onClick={handleComplete} variant='primary' size='medium' type='solid'>
					{t('loading.continue')}
				</Button>
			)}
		</div>
	);
};

Loading.propTypes = {
	messages: PropTypes.arrayOf(PropTypes.string),
	question: PropTypes.string,
	progressDuration: PropTypes.number,
	className: PropTypes.string,
	onComplete: PropTypes.func,
	completed: PropTypes.bool,
};

export default Loading;
